import { useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import log from 'loglevel'
import { RiskServicePromiseClient } from '@trustero/trustero-api-web/lib/risk/risk_grpc_web_pb'
import {
  UpsertRiskCategoryOptionRequest,
  RiskCategoryOption,
  GetRiskCategoryOptionResponse,
  CreateOrUpdateRisksRequest,
  DeleteRiskCategoryOptionRequest,
  DeleteRisksRequest,
  GetRisksRequest,
  GetRisksResponse,
  ToggleRiskLinksRequest,
  RISK_LINK_TYPE,
  RiskLink,
  Risk,
  RISK_MATH,
  RISK_STATUS,
} from '@trustero/trustero-api-web/lib/risk/risk_pb'
import { GrpcResponse, NewGrpcResponse } from 'src/components/async/hooks/types'
import { useSwrImmutableGrpc } from 'src/components/async/useSwrImmutableGrpc'
import { SwrGrpcResponse } from 'src/components/async/useSwrGrpc'
import { useGrpcRevalidateByMethod } from 'src/components/async'
import { Empty } from 'google-protobuf/google/protobuf/empty_pb'
import { useAnalytics } from 'src/analytics/useAnalytics'
import { AttachmentPromiseClient } from '@trustero/trustero-api-web/lib/attachment/attachment_grpc_web_pb'
import FileSaver from 'file-saver'
import { showInfoToast } from 'src/Utils/helpers/toast'
import {
  useHasRiskCategoryConfiguration,
  useHasCustomRiskStatuses,
} from 'src/context/Gatekeeper'
import { BasicDropdownItem } from 'src/components/Reusable/BasicDropdown/BasicDropdown.types'
import { useRiskStatuses } from 'src/pages/AccountSettings/AccountSettings.hooks'
import palette from 'src/designSystem/variables/palette'
import { useInAudit } from 'src/context/AuditContext'
import { GridColumnSortRisks } from 'src/components/Reusable/Grid/GridColumnSort/GridColumnSort.constants'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { useCanPerformAction } from 'src/app/AppAuth/AppAuth.hooks'
import { useAuthorizedGrpcClient } from '../../adapter/grpcClient'
import {
  getRisksRequest,
  useFilterControlIdsByAudit,
  updatedRiskRpcData,
} from './risks.helpers'
import { useRisksContext } from './context/RisksContext'
import { getRiskMathCalculation, getRiskStatus } from './risks.helpers'
import {
  RISK_MATH_VALUES,
  RISK_CATEGORY_LABEL,
  RiskFields,
  RISK_STATUS_FILTER,
  RISK_STATUS_LABEL,
  RISK_STATUS_BG,
  RISK_STATUS_BORDER,
} from './risks.constants'

export interface CategoryOption {
  value: number
  name: string
  sortOrder?: number
}

export const useInvalidateRisksCache = (): (() => Promise<void>) => {
  const mutateFunc = useGrpcRevalidateByMethod()

  return useCallback(async () => {
    try {
      await Promise.all([
        mutateFunc(RiskServicePromiseClient.prototype.getRisks),
      ])
    } catch (error) {
      log.error('Error when invalidating controls cache', error)
    }
  }, [mutateFunc])
}

export const useRisks = (
  request: GetRisksRequest = new GetRisksRequest(),
  shouldFetch = true,
  config?: SwrGrpcResponse,
): GrpcResponse<GetRisksResponse> => {
  const { auditId } = useInAudit()
  if (auditId) {
    request.setAuditId(auditId)
  }
  const { response } = useSwrImmutableGrpc(
    RiskServicePromiseClient.prototype.getRisks,
    request,
    shouldFetch,
    config,
  )

  useFilterControlIdsByAudit(response.data?.getRisksList())
  return NewGrpcResponse(response)
}

// TODO: Come back and clean this up to use instant lookup
/**
 * Fetches a single risk from the cache.
 * @param {string} riskId - The ID of the risk to fetch.
 * @returns {Risk | null | false} The risk response, or null if it is still loading, or false if it was not found.
 */
export const useRisk = (riskId: string): Risk | null | false => {
  const { data, isLoading } = useRisks(new GetRisksRequest(), true, {
    ignoreAuditContext: true,
  })

  if (isLoading || !data) {
    return null
  }

  return (
    data.getRisksList().find((risk: Risk) => {
      return risk.getId() === riskId || risk.getCustomId() === riskId
    }) || false
  )
}

export const useCreateOrUpdateRisks = (
  isBulk?: boolean,
  shouldMutate = true,
): ((risks: Risk[]) => Promise<void>) => {
  const { track, events } = useAnalytics()
  const mutator = useInvalidateRisksCache()
  const riskClient = useAuthorizedGrpcClient(RiskServicePromiseClient)

  const createOrUpdateRisk = async (risks: Risk[]) => {
    const req = new CreateOrUpdateRisksRequest().setRisksList(risks)
    await riskClient.createOrUpdateRisks(req)
    shouldMutate && (await mutator())
    // Check if Create or Update & fire an event to track user attempting to create a risk
    const eventKey = risks[0]?.getId() ? events.UPDATE_RISK : events.CREATE_RISK
    const eventProps =
      eventKey === events.CREATE_RISK ? { type: isBulk ? 'csv' : 'manual' } : {}
    track(eventKey, eventProps)
    return
  }
  return createOrUpdateRisk
}

export const useToggleRiskReviewed = (
  risk: Risk,
): {
  isReviewed: boolean
  toggleReviewed: (e?: React.MouseEvent) => Promise<void>
  canUpdateRisk: boolean
} => {
  const updateRisks = useCreateOrUpdateRisks()
  const canUpdateRisk = useCanPerformAction<Risk.AsObject>(
    CAPABILITY.CAN_UPDATE_RISKS,
    risk.toObject(),
  )
  const lastReviewedAt = risk.getLastReviewedAt()
  const isReviewed = !!lastReviewedAt

  const toggleReviewed = useCallback(
    async (e?: React.MouseEvent) => {
      try {
        e?.preventDefault()
        const riskToUpdate = risk
          .cloneMessage()
          .setLastReviewedAt(isReviewed ? 0 : Date.now())

        await updateRisks([riskToUpdate])
      } catch (err) {
        log.error('Failed to toggle risk reviewed status', err)
      }
    },
    [risk, updateRisks, isReviewed],
  )

  return { isReviewed, toggleReviewed, canUpdateRisk }
}

export const useDeleteRisks = (): ((riskIds: string[]) => Promise<void>) => {
  const { track, events } = useAnalytics()
  const mutator = useInvalidateRisksCache()
  const riskClient = useAuthorizedGrpcClient(RiskServicePromiseClient)

  const deleteRisks = async (riskIds: string[]) => {
    const req = new DeleteRisksRequest().setRiskIdsList(riskIds)
    await riskClient.deleteRisks(req)
    await mutator()
    track(events.DELETE_RISK)
    return
  }
  return deleteRisks
}

export const useDownloadRiskCSV = (): (() => Promise<void>) => {
  const riskClient = useAuthorizedGrpcClient(AttachmentPromiseClient)

  return async () => {
    try {
      const res = await riskClient.getRiskRegisterCSVDownload(new Empty())
      const csv = res.getCsv()
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      await FileSaver.saveAs(blob, 'risk-register.csv')
    } catch (err) {
      log.error('Error downloading risk register csv', err)
      showInfoToast('Error downloading risk register csv. Please try again.')
    }
  }
}

export const useRiskCategoryOptions = (): (() => Promise<void>) => {
  const useCategoryConfig = useHasRiskCategoryConfiguration()
  const riskClient = useAuthorizedGrpcClient(RiskServicePromiseClient)
  const {
    setCategoryOptions,
    setIsCategoryOptionsLoading,
    setIsCategoryOptionsInitialized,
  } = useRisksContext()

  return useCallback(async () => {
    if (!useCategoryConfig) return

    setIsCategoryOptionsLoading(true)
    try {
      const res: GetRiskCategoryOptionResponse =
        await riskClient.getRiskCategoryOption(new Empty())
      const riskCategories = res.getOptionsList().map((option) => ({
        name: option.getName(),
        value: option.getValue(),
        sortOrder: option.getSortOrder(),
      }))
      setCategoryOptions(riskCategories)
    } catch (err) {
      log.error('Error fetching risk category options', err)
      showInfoToast('Error fetching risk category options. Please try again.')
      throw err
    } finally {
      setIsCategoryOptionsLoading(false)
      setIsCategoryOptionsInitialized(true)
    }
  }, [
    useCategoryConfig,
    riskClient,
    setCategoryOptions,
    setIsCategoryOptionsLoading,
    setIsCategoryOptionsInitialized,
  ])
}

export const useUpsertRiskCategoryOptions = (): ((
  customCategories: CategoryOption[],
) => Promise<void>) => {
  const useCategoryConfig = useHasRiskCategoryConfiguration()
  const riskClient = useAuthorizedGrpcClient(RiskServicePromiseClient)

  return useCallback(
    async (customCategories: CategoryOption[]) => {
      if (!useCategoryConfig) return

      try {
        const options = customCategories.map((category) =>
          new RiskCategoryOption()
            .setName(category.name)
            .setValue(category.value)
            .setSortOrder(category.sortOrder || 0),
        )
        await riskClient.upsertRiskCategoryOption(
          new UpsertRiskCategoryOptionRequest().setOptionList(options),
        )
      } catch (err) {
        log.error('Error upserting risk category options', err)
        showInfoToast(
          'Error upserting risk category options. Please try again.',
        )
        throw err
      }
    },
    [useCategoryConfig, riskClient],
  )
}

export const useDeleteRiskCategoryOptions = (): ((
  categoryIds: number[],
) => Promise<void>) => {
  const useCategoryConfig = useHasRiskCategoryConfiguration()
  const riskClient = useAuthorizedGrpcClient(RiskServicePromiseClient)

  return useCallback(
    async (categoryIds: number[]) => {
      if (!useCategoryConfig) return

      try {
        await riskClient.deleteRiskCategoryOption(
          new DeleteRiskCategoryOptionRequest().setIdList(categoryIds),
        )
      } catch (err) {
        log.error('Error deleting risk category options', err)
        showInfoToast('Error deleting risk category options. Please try again.')
        throw err
      }
    },
    [useCategoryConfig, riskClient],
  )
}

/**
 * If you are using this, add a try catch in the parent component to handle errors!!!
 */
export const useToggleLinks = (): (({
  links,
  type,
}: {
  links: RiskLink[]
  type: RISK_LINK_TYPE
}) => Promise<Empty>) => {
  const { track, events } = useAnalytics()
  const mutator = useInvalidateRisksCache()
  const riskClient = useAuthorizedGrpcClient(RiskServicePromiseClient)

  return async ({
    links,
    type,
  }: {
    links: RiskLink[]
    type: RISK_LINK_TYPE
  }) => {
    const req = new ToggleRiskLinksRequest().setLinksList(links).setType(type)
    const res = await riskClient.toggleRiskLinks(req)
    await mutator()
    const eventKey =
      type === RISK_LINK_TYPE.LINK ? events.LINK_CONTROL : events.UNLINK_CONTROL
    track(eventKey)
    return res
  }
}

export const useRisksRequest = (): GetRisksRequest => {
  const location = useLocation()
  const { sortColumn, sortType } = useRisksContext()

  return useMemo(
    () => getRisksRequest(location.search, sortColumn, sortType),
    [location.search, sortColumn, sortType],
  )
}

export const useActualRiskCalculation = (
  risk: Risk,
): {
  inherentRisk: number
  inherentRiskValue: number
  targetResidualRiskValue: number
  targetRiskReductionValue: number
  perControlRiskReductionValue: number
  actualRiskReductionValue: number
  actualRiskValue: number
  actualRiskReduction: RISK_MATH
  controlEffectiveness: number
} => {
  const controlIdsStr = risk.getControlIds()
  const impact = risk.getAdverseImpact()
  const likelihood = risk.getLikelihood()
  const targetAdverseImpact = risk.getTargetAdverseImpact()
  const targetLikelihood = risk.getTargetLikelihood()
  const targetResidualRisk = getRiskMathCalculation(
    targetAdverseImpact,
    targetLikelihood,
  )
  const successfulControls = risk.getSuccessfulControls()
  const numSuccessfulControls = successfulControls.split(',').length

  const controlIds = controlIdsStr.split(',')
  const inherentRisk = getRiskMathCalculation(impact, likelihood)
  const inherentRiskValue = RISK_MATH_VALUES[inherentRisk]
  const targetResidualRiskValue = RISK_MATH_VALUES[targetResidualRisk]
  const targetRiskReductionValue = inherentRiskValue - targetResidualRiskValue
  const perControlRiskReductionValue =
    controlIdsStr.length > 0 ? targetRiskReductionValue / controlIds.length : 0
  const actualRiskReductionValue =
    successfulControls.length > 0
      ? perControlRiskReductionValue * numSuccessfulControls
      : 0
  const actualRiskValue = inherentRiskValue - actualRiskReductionValue
  const actualRiskReduction = getRiskStatus(actualRiskValue)
  const controlEffectiveness =
    controlIdsStr.length > 0 && successfulControls.length > 0
      ? numSuccessfulControls / controlIds.length
      : 0

  return {
    inherentRisk,
    inherentRiskValue,
    targetResidualRiskValue,
    targetRiskReductionValue,
    perControlRiskReductionValue,
    actualRiskReductionValue,
    actualRiskReduction,
    controlEffectiveness,
    actualRiskValue,
  }
}

export const useRiskComputedValues = (): {
  getCategoryName: (risk: Risk) => string
  sortRisk: (a: Risk, b: Risk) => number
} => {
  const hasRiskCategoryConfiguration = useHasRiskCategoryConfiguration()
  const { categoryOptions, sortColumn, sortType } = useRisksContext()

  const GetCalculatedRisk = (risk: Risk) => {
    const { inherentRisk, actualRiskReduction } = useActualRiskCalculation(risk)

    return {
      inherentRisk,
      actualRiskReduction,
    }
  }

  const getCategoryName = useCallback(
    (risk: Risk) => {
      const category = risk ? risk.getCategory() : 0
      if (hasRiskCategoryConfiguration) {
        const categoryOption = categoryOptions.find(
          (option) => option.value === category,
        )
        return categoryOption ? categoryOption.name : ''
      } else {
        return RISK_CATEGORY_LABEL[category] || ''
      }
    },
    [categoryOptions, hasRiskCategoryConfiguration],
  )

  const compareValues = (
    a: string | number | RISK_MATH,
    b: string | number | RISK_MATH,
    sortType: string,
  ): number => {
    if (sortType === 'asc') {
      return a < b ? -1 : a > b ? 1 : 0
    } else {
      return a < b ? 1 : a > b ? -1 : 0
    }
  }

  const sortRisk = useCallback(
    (a: Risk, b: Risk): number => {
      if (sortColumn === GridColumnSortRisks.Category) {
        const aCategoryName = getCategoryName(a)
        const bCategoryName = getCategoryName(b)
        return compareValues(aCategoryName, bCategoryName, sortType)
      } else if (sortColumn === GridColumnSortRisks['Inherent risk']) {
        const aInherentRisk = GetCalculatedRisk(a).inherentRisk
        const bInherentRisk = GetCalculatedRisk(b).inherentRisk
        return compareValues(aInherentRisk, bInherentRisk, sortType)
      } else if (sortColumn === GridColumnSortRisks['Current residual risk']) {
        const aCurrentResidualRisk = GetCalculatedRisk(a).actualRiskReduction
        const bCurrentResidualRisk = GetCalculatedRisk(b).actualRiskReduction
        return compareValues(
          aCurrentResidualRisk,
          bCurrentResidualRisk,
          sortType,
        )
      } else if (sortColumn === GridColumnSortRisks.Controls) {
        const aControlCount = a.getControlIds()
          ? a.getControlIds().split(',').length
          : 0
        const bControlCount = b.getControlIds()
          ? b.getControlIds().split(',').length
          : 0
        return compareValues(aControlCount, bControlCount, sortType)
      }
      return 0
    },
    [sortColumn, getCategoryName, sortType],
  )

  return {
    getCategoryName,
    sortRisk,
  }
}

// Shown when custom statuses are on but a risk has no valid one (its option was
// deleted, or it never had one). We never fall back to the fixed enum in that case.
const NO_STATUS_ITEM: BasicDropdownItem = { name: 'No status', value: '' }

export interface RiskStatusSelection {
  items: BasicDropdownItem[]
  selectedItem: BasicDropdownItem
  colorsFor: (value: string | number) => { background: string; border: string }
  toUpdatedRisk: (item: BasicDropdownItem) => Risk
  // True when custom statuses are enabled but the account has none (all deleted):
  // nothing is selectable, so consumers should disable the status dropdown.
  noOptions: boolean
}

// useRiskStatusSelection centralizes the custom-vs-enum status logic shared by the risk
// grid and show page. With the flag on, options are the account's custom statuses
// (BasicDropdownItem value = status id, a string); with it off, the fixed RISK_STATUS
// enum (value = the enum number). The effective status is the custom status when the
// risk has one, else the enum.
export const useRiskStatusSelection = (risk: Risk): RiskStatusSelection => {
  const { data: customStatuses } = useRiskStatuses()
  const hasCustomStatuses = useHasCustomRiskStatuses()
  const enumStatus = risk.getStatus()
  const customStatusId = risk.getCustomStatusId()
  const customList = useMemo(
    () => customStatuses?.getItemsList() ?? [],
    [customStatuses],
  )

  const currentCustom =
    hasCustomStatuses && customStatusId
      ? customList.find((s) => s.getId() === customStatusId)
      : undefined

  const items = useMemo<BasicDropdownItem[]>(
    () =>
      hasCustomStatuses
        ? customList
            .filter((s) => s.getName())
            .map((s) => ({ name: s.getName(), value: s.getId() }))
        : RISK_STATUS_FILTER.map((o) => ({ name: o.name, value: o.value })),
    [hasCustomStatuses, customList],
  )

  // With custom statuses enabled we never fall back to the fixed enum: show the
  // risk's custom status, else a neutral empty state. The enum path applies only
  // when the feature is off.
  const selectedItem: BasicDropdownItem = currentCustom
    ? { name: currentCustom.getName(), value: currentCustom.getId() }
    : hasCustomStatuses
    ? NO_STATUS_ITEM
    : { name: RISK_STATUS_LABEL[enumStatus], value: enumStatus }

  const colorsFor = (
    value: string | number,
  ): { background: string; border: string } => {
    if (typeof value === 'string') {
      const custom = customList.find((s) => s.getId() === value)
      return {
        background: custom?.getColor() || 'white',
        border: custom?.getBorderColor() || palette.neutral['200'],
      }
    }
    return {
      background: RISK_STATUS_BG[value as RISK_STATUS] ?? 'white',
      border:
        RISK_STATUS_BORDER[value as RISK_STATUS] ?? palette.neutral['200'],
    }
  }

  const toUpdatedRisk = (item: BasicDropdownItem): Risk =>
    typeof item.value === 'string'
      ? updatedRiskRpcData(risk, RiskFields.customStatusId, item.value)
      : updatedRiskRpcData(risk, RiskFields.status, item.value as RISK_STATUS)

  return {
    items,
    selectedItem,
    colorsFor,
    toUpdatedRisk,
    noOptions: hasCustomStatuses && items.length === 0,
  }
}
